import React from 'react';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Privacy Policy
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Your privacy is important to us. Learn how we collect, use, and protect your information.
          </p>
          <p className="text-sm text-gray-500 mt-4">
            Last updated: January 2025
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="bg-white rounded-xl shadow-lg p-8">
          <div className="prose max-w-none">
            
            <h2 className="text-2xl font-bold text-gray-900 mb-6">1. Information We Collect</h2>
            
            <h3 className="text-xl font-semibold text-gray-900 mb-4">1.1 Personal Information</h3>
            <p className="text-gray-700 mb-4">
              We collect information you provide directly to us, including:
            </p>
            <ul className="list-disc list-inside text-gray-700 mb-6 space-y-2">
              <li>Name and contact information (email address, phone number)</li>
              <li>Account credentials and profile information</li>
              <li>Payment and billing information</li>
              <li>Course progress and completion data</li>
              <li>Communications with our support team</li>
              <li>Survey responses and feedback</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-4">1.2 Automatically Collected Information</h3>
            <p className="text-gray-700 mb-4">
              We automatically collect certain information when you use our services:
            </p>
            <ul className="list-disc list-inside text-gray-700 mb-6 space-y-2">
              <li>Device information (IP address, browser type, operating system)</li>
              <li>Usage data (pages visited, time spent, features used)</li>
              <li>Cookies and similar tracking technologies</li>
              <li>Log files and analytics data</li>
            </ul>

            <h2 className="text-2xl font-bold text-gray-900 mb-6 mt-12">2. How We Use Your Information</h2>
            
            <h3 className="text-xl font-semibold text-gray-900 mb-4">2.1 Service Provision</h3>
            <p className="text-gray-700 mb-6">
              We use your information to provide, maintain, and improve our educational services, process transactions, 
              deliver course content, and provide customer support.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-4">2.2 Communication</h3>
            <p className="text-gray-700 mb-6">
              We may use your contact information to send you important updates about your courses, 
              respond to your inquiries, and provide technical support.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-4">2.3 Marketing and Promotions</h3>
            <p className="text-gray-700 mb-6">
              With your consent, we may send you promotional materials about new courses, 
              special offers, and educational content that may interest you.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mb-6 mt-12">3. Information Sharing and Disclosure</h2>
            
            <h3 className="text-xl font-semibold text-gray-900 mb-4">3.1 Third-Party Service Providers</h3>
            <p className="text-gray-700 mb-6">
              We may share your information with trusted third-party service providers who assist us in 
              operating our website, processing payments, or providing customer support. These parties 
              are bound by confidentiality agreements and are prohibited from using your information 
              for any other purpose.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-4">3.2 Legal Requirements</h3>
            <p className="text-gray-700 mb-6">
              We may disclose your information if required by law, court order, or government regulation, 
              or if we believe disclosure is necessary to protect our rights, property, or safety, or that of others.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-4">3.3 Business Transfers</h3>
            <p className="text-gray-700 mb-6">
              In the event of a merger, acquisition, or sale of assets, your information may be transferred 
              as part of the transaction, subject to the same privacy protections.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mb-6 mt-12">4. Data Security</h2>
            
            <p className="text-gray-700 mb-6">
              We implement appropriate technical and organizational security measures to protect your personal 
              information against unauthorized access, alteration, disclosure, or destruction. These measures include:
            </p>
            <ul className="list-disc list-inside text-gray-700 mb-6 space-y-2">
              <li>SSL encryption for data transmission</li>
              <li>Secure servers and databases</li>
              <li>Regular security audits and updates</li>
              <li>Access controls and authentication</li>
              <li>Employee training on data protection</li>
            </ul>

            <h2 className="text-2xl font-bold text-gray-900 mb-6 mt-12">5. Your Rights and Choices</h2>
            
            <h3 className="text-xl font-semibold text-gray-900 mb-4">5.1 Access and Update</h3>
            <p className="text-gray-700 mb-6">
              You can access and update your personal information through your account settings or by 
              contacting our support team.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-4">5.2 Data Portability</h3>
            <p className="text-gray-700 mb-6">
              You have the right to receive a copy of your personal data in a structured, 
              machine-readable format.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-4">5.3 Deletion</h3>
            <p className="text-gray-700 mb-6">
              You may request deletion of your personal information, subject to certain legal and 
              contractual obligations.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-4">5.4 Marketing Communications</h3>
            <p className="text-gray-700 mb-6">
              You can opt out of marketing communications at any time by clicking the unsubscribe 
              link in our emails or contacting us directly.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mb-6 mt-12">6. Cookies and Tracking Technologies</h2>
            
            <p className="text-gray-700 mb-6">
              We use cookies and similar technologies to enhance your experience, analyze usage patterns, 
              and provide personalized content. You can control cookie settings through your browser preferences.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mb-6 mt-12">7. International Data Transfers</h2>
            
            <p className="text-gray-700 mb-6">
              Your information may be transferred to and processed in countries other than your own. 
              We ensure appropriate safeguards are in place to protect your data in accordance with 
              applicable privacy laws.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mb-6 mt-12">8. Children's Privacy</h2>
            
            <p className="text-gray-700 mb-6">
              Our services are not intended for children under 13 years of age. We do not knowingly 
              collect personal information from children under 13. If we become aware that we have 
              collected such information, we will take steps to delete it promptly.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mb-6 mt-12">9. Changes to This Policy</h2>
            
            <p className="text-gray-700 mb-6">
              We may update this Privacy Policy from time to time. We will notify you of any material 
              changes by posting the new policy on this page and updating the "Last updated" date. 
              Your continued use of our services after such changes constitutes acceptance of the updated policy.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mb-6 mt-12">10. Contact Us</h2>
            
            <p className="text-gray-700 mb-6">
              If you have any questions about this Privacy Policy or our data practices, please contact us:
            </p>
            
            <div className="bg-gray-50 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Privacy Contact Information</h3>
              <div className="space-y-2 text-gray-700">
                <p><strong>Email:</strong> karabacakhilal1.1@gmail.com</p>
              </div>
            </div>

            <div className="mt-8 p-4 bg-blue-50 rounded-lg">
              <p className="text-blue-800 text-sm">
                <strong>Note:</strong> This Privacy Policy complies with applicable privacy laws including GDPR, 
                CCPA, and other relevant regulations. We are committed to protecting your privacy and 
                maintaining the highest standards of data protection.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
